// src/services/campaignsService.js
import { apiRequest } from "./apiService";
import { buildQueryString } from "./queryString";

const encId = (id) => encodeURIComponent(String(id == null ? "" : id).trim());

const toStr = (v) => String(v == null ? "" : v).trim();

const toNum = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const normalizeHandle = (h) => toStr(h).replace(/^@+/, "");

const normalizeListCampaignsParams = (params = {}) => {
  const p = { ...(params || {}) };

  const cursor =
    p.cursor ??
    p.next_cursor ??
    p.nextCursor ??
    p.last_key ??
    p.lastKey ??
    null;

  if (cursor != null && toStr(cursor) !== "") {
    p.cursor = cursor;
  } else {
    delete p.cursor;
  }

  delete p.next_cursor;
  delete p.nextCursor;
  delete p.last_key;
  delete p.lastKey;

  return p;
};

const normalizeCreatorRefs = (raw) => {
  if (Array.isArray(raw?.creatorRefs)) return raw.creatorRefs;
  if (Array.isArray(raw?.influencerRefs)) return raw.influencerRefs;
  return [];
};

const normalizeOfferRefs = (raw) =>
  Array.isArray(raw?.offerRefs) ? raw.offerRefs : [];

const normalizeInviteRefs = (raw) =>
  Array.isArray(raw?.inviteRefs) ? raw.inviteRefs : [];

const normalizeInviteMetrics = (raw) => {
  const metrics =
    raw?.inviteMetrics && typeof raw.inviteMetrics === "object"
      ? raw.inviteMetrics
      : {};

  return {
    total: toNum(metrics.total, 0),
    active: toNum(metrics.active, 0),
    shared: toNum(metrics.shared, 0),
    draft: toNum(metrics.draft, 0),
    sent: toNum(metrics.sent, 0),
    accepted: toNum(metrics.accepted, 0),
    live: toNum(metrics.live, 0),
    completed: toNum(metrics.completed, 0),
    rejected: toNum(metrics.rejected, 0),
  };
};

const normalizeWorkflowSummary = (raw, fallback = {}) => {
  const workflow =
    raw?.workflowSummary && typeof raw.workflowSummary === "object"
      ? raw.workflowSummary
      : {};

  return {
    totalCreators: toNum(
      workflow.totalCreators,
      toNum(fallback.totalCreators, 0)
    ),
    totalOffers: toNum(workflow.totalOffers, toNum(fallback.totalOffers, 0)),
    activeOffers: toNum(
      workflow.activeOffers,
      toNum(fallback.activeOffers, 0)
    ),
    totalInvites: toNum(
      workflow.totalInvites,
      toNum(fallback.totalInvites, 0)
    ),
    activeInvites: toNum(
      workflow.activeInvites,
      toNum(fallback.activeInvites, 0)
    ),
    sharedLinks: toNum(workflow.sharedLinks, toNum(fallback.sharedLinks, 0)),
    redemptions: toNum(
      workflow.redemptions,
      toNum(fallback.redemptions, 0)
    ),
  };
};

const normalizeRoiMetrics = (raw) => {
  const roi =
    raw?.roiMetrics && typeof raw.roiMetrics === "object" ? raw.roiMetrics : {};

  return {
    impressions: toNum(roi.impressions, 0),
    reach: toNum(roi.reach, 0),
    clicks: toNum(roi.clicks, 0),
    uniqueClicks: toNum(roi.uniqueClicks, 0),
    redemptions: toNum(roi.redemptions, 0),
    revenue: toNum(roi.revenue, 0),
    cost: toNum(roi.cost, 0),
    roas: toNum(roi.roas, 0),
  };
};

const normalizeInfluencerItems = (items) => {
  if (!Array.isArray(items) || items.length === 0) return [];

  const pickUsername = (x) => {
    const u = toStr(x?.username || x?.handle || x?.name || "");
    return u ? u.replace(/^@+/, "") : "";
  };

  const pickInfluencerId = (x) =>
    toStr(
      x?.influencerId ||
        x?.influencer_id ||
        x?.creatorId ||
        x?.creator_id ||
        x?.id ||
        x?._id ||
        ""
    );

  const pickProfileUrl = (x) =>
    toStr(x?.profileUrl || x?.profile_url || x?.url || x?.link || "");

  const pickProfileImageUrl = (x) =>
    toStr(
      x?.profileImageUrl ||
        x?.profile_image_url ||
        x?.avatarUrl ||
        x?.avatar_url ||
        x?.avatar ||
        ""
    );

  const out = [];
  const seen = new Set();

  for (const x of items) {
    if (typeof x === "string" || typeof x === "number") {
      const username = normalizeHandle(x);
      if (!username) continue;

      const key = username.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);

      out.push({
        username,
        handle: username,
        platform: "instagram",
        profileUrl: `https://instagram.com/${username}`,
        profile_url: `https://instagram.com/${username}`,
      });
      continue;
    }

    if (!x || typeof x !== "object" || Array.isArray(x)) continue;

    const username = pickUsername(x);
    const influencerId = pickInfluencerId(x);

    if (!username && !influencerId) continue;

    const dedupeKey = (username || influencerId).toLowerCase();
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);

    const profileUrl =
      pickProfileUrl(x) || (username ? `https://instagram.com/${username}` : "");
    const profileImageUrl = pickProfileImageUrl(x);

    const followers =
      typeof x.followers === "number"
        ? x.followers
        : typeof x.followerCount === "number"
        ? x.followerCount
        : (() => {
            const n = Number(x.followers ?? x.followerCount ?? x.followers_count);
            return Number.isFinite(n) ? n : undefined;
          })();

    const payload = {
      influencerId: influencerId || undefined,
      id: influencerId || undefined,
      username: username || undefined,
      handle: username || undefined,
      platform: toStr(x.platform) || "instagram",
      profileUrl: profileUrl || undefined,
      profile_url: profileUrl || undefined,
      profileImageUrl: profileImageUrl || undefined,
      profile_image_url: profileImageUrl || undefined,
      followers,
      full_name: x.full_name || x.fullName || x.name || undefined,
      source: x.source ? toStr(x.source) : undefined,
      addedAt: x.addedAt ? toStr(x.addedAt) : undefined,
    };

    Object.keys(payload).forEach(
      (k) => payload[k] === undefined && delete payload[k]
    );

    out.push(payload);
  }

  return out;
};

const normalizeCreateCampaignInfluencers = (values) => {
  if (!Array.isArray(values) || values.length === 0) return [];
  return normalizeInfluencerItems(values);
};

const normalizeHandles = (values) => {
  if (!Array.isArray(values)) return [];

  const out = [];
  const seen = new Set();

  for (const x of values) {
    let h = "";

    if (typeof x === "string" || typeof x === "number") {
      h = normalizeHandle(x);
    } else if (x && typeof x === "object" && !Array.isArray(x)) {
      h = normalizeHandle(
        x.username ||
          x.handle ||
          x.name ||
          x.influencerId ||
          x.creatorId ||
          x.id ||
          x.external_id ||
          ""
      );
    }

    if (!h) continue;

    const key = h.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(h);
  }

  return out;
};

const normalizeCampaign = (raw = {}) => {
  const creatorRefs = normalizeCreatorRefs(raw);
  const offerRefs = normalizeOfferRefs(raw);
  const inviteRefs = normalizeInviteRefs(raw);
  const influencers = Array.isArray(raw.influencers) ? raw.influencers : [];

  const influencerCount = toNum(
    raw.influencerCount,
    influencers.length || creatorRefs.length
  );

  const offersCount = toNum(raw.offersCount, offerRefs.length);
  const activeOffersCount = toNum(raw.activeOffersCount, 0);

  const invitesCount = toNum(raw.invitesCount, inviteRefs.length);
  const activeInvitesCount = toNum(raw.activeInvitesCount, 0);
  const sharedLinksCount = toNum(raw.sharedLinksCount, 0);

  const redemptionsCount = toNum(raw.redemptionsCount, 0);
  const roiMetrics = normalizeRoiMetrics(raw);
  const inviteMetrics = normalizeInviteMetrics(raw);

  const workflowSummary = normalizeWorkflowSummary(raw, {
    totalCreators: influencerCount,
    totalOffers: offersCount,
    activeOffers: activeOffersCount,
    totalInvites: invitesCount,
    activeInvites: activeInvitesCount,
    sharedLinks: sharedLinksCount,
    redemptions: redemptionsCount || roiMetrics.redemptions,
  });

  return {
    ...raw,
    campaignId: raw.campaignId || raw.id || "",
    id: raw.id || raw.campaignId || "",
    name: raw.name || "",
    goal: raw.goal || "",
    status: raw.status || "",

    influencers,
    creatorRefs,
    influencerRefs: creatorRefs,
    influencerCount,

    budget: raw.budget ?? null,
    startDate: raw.startDate || null,
    endDate: raw.endDate || null,

    offersCount,
    activeOffersCount,
    offerRefs,

    invitesCount,
    activeInvitesCount,
    sharedLinksCount,
    inviteRefs,

    inviteDraftCount: toNum(raw.inviteDraftCount, 0),
    inviteSentCount: toNum(raw.inviteSentCount, 0),
    inviteAcceptedCount: toNum(raw.inviteAcceptedCount, 0),
    inviteLiveCount: toNum(raw.inviteLiveCount, 0),
    inviteCompletedCount: toNum(raw.inviteCompletedCount, 0),
    inviteRejectedCount: toNum(raw.inviteRejectedCount, 0),

    redemptionsCount,
    roiMetrics,
    inviteMetrics,
    workflowSummary,
    inviteMetricsSource: raw.inviteMetricsSource || raw.source || "none",

    createdAt: raw.createdAt || "",
    updatedAt: raw.updatedAt || null,
    idempotent: Boolean(raw.idempotent),
  };
};

const normalizeCampaignMutationResult = (raw = {}) => {
  const normalizedCampaign = normalizeCampaign(raw);

  return {
    ...raw,
    ...normalizedCampaign,
    added: toNum(raw.added, 0),
    skipped: toNum(raw.skipped, 0),
    removed: Array.isArray(raw.removed) ? raw.removed : [],
    failed: Array.isArray(raw.failed) ? raw.failed : [],
  };
};

const normalizeListCampaignsResponse = (raw = {}) => {
  const nextCursor = raw?.next_cursor ?? raw?.nextCursor ?? raw?.cursor ?? null;

  return {
    ...raw,
    count: toNum(raw?.count, Array.isArray(raw?.items) ? raw.items.length : 0),
    next_cursor: nextCursor,
    nextCursor,
    items: Array.isArray(raw?.items) ? raw.items.map(normalizeCampaign) : [],
  };
};

export const createCampaign = async (payload) => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw new Error("Valid payload is required to create a campaign.");
  }

  const body = { ...payload };

  if (Array.isArray(body.influencers)) {
    body.influencers = normalizeCreateCampaignInfluencers(body.influencers);
  } else {
    body.influencers = [];
  }

  const res = await apiRequest("/campaigns", "POST", body);
  return normalizeCampaign(res || {});
};

export const listCampaigns = async (params = {}) => {
  const qs = buildQueryString(normalizeListCampaignsParams(params));
  const res = await apiRequest(`/campaigns${qs}`, "GET");
  return normalizeListCampaignsResponse(res || {});
};

export const getCampaign = async (campaignId) => {
  if (!toStr(campaignId)) {
    throw new Error("campaignId is required.");
  }

  const res = await apiRequest(`/campaigns/${encId(campaignId)}`, "GET");
  return normalizeCampaign(res || {});
};

export const patchCampaign = async (campaignId, payload) => {
  if (!toStr(campaignId)) {
    throw new Error("campaignId is required.");
  }
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw new Error("Valid payload is required to patch campaign.");
  }

  const res = await apiRequest(
    `/campaigns/${encId(campaignId)}`,
    "PATCH",
    payload
  );
  return normalizeCampaign(res || {});
};

export const deleteCampaign = async (campaignId) => {
  if (!toStr(campaignId)) {
    throw new Error("campaignId is required.");
  }

  return apiRequest(`/campaigns/${encId(campaignId)}`, "DELETE");
};

export const addInfluencersToCampaign = async (campaignId, items) => {
  if (!toStr(campaignId)) {
    throw new Error("campaignId is required.");
  }
  if (!Array.isArray(items) || items.length === 0) {
    throw new Error("items[] is required.");
  }

  const normalized = normalizeInfluencerItems(items);
  if (normalized.length === 0) {
    throw new Error("items[] must contain valid influencer values.");
  }

  const res = await apiRequest(
    `/campaigns/${encId(campaignId)}/influencers`,
    "POST",
    {
      items: normalized,
    }
  );

  return normalizeCampaignMutationResult(res || {});
};

export const removeInfluencerFromCampaign = async (campaignId, handle) => {
  if (!toStr(campaignId)) {
    throw new Error("campaignId is required.");
  }

  const h = normalizeHandle(handle);
  if (!h) {
    throw new Error("handle is required.");
  }

  const res = await apiRequest(
    `/campaigns/${encId(campaignId)}/influencers/${encodeURIComponent(h)}`,
    "DELETE"
  );

  return normalizeCampaignMutationResult(res || {});
};

export const removeInfluencersFromCampaign = async (campaignId, handles) => {
  if (!toStr(campaignId)) {
    throw new Error("campaignId is required.");
  }

  const hs = normalizeHandles(handles);
  if (hs.length === 0) {
    throw new Error("handles[] is required.");
  }

  const res = await apiRequest(
    `/campaigns/${encId(campaignId)}/influencers/remove`,
    "POST",
    { handles: hs }
  );

  return normalizeCampaignMutationResult(res || {});
};